import { useEffect, useState, useRef } from 'react';
import dayjs from 'dayjs';
import {
    Box, Button, Typography, Stack, Divider, useTheme, Fade, Zoom, Container, TextField, InputAdornment,
    IconButton, Tooltip, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, TableFooter, styled,
    Breadcrumbs,
    Link,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import PrintIcon from '@mui/icons-material/Print';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../lib/axios';
import { toast, ToastContainer } from 'react-toastify';
import { useReactToPrint } from 'react-to-print';
import { useSelector } from 'react-redux';
import { selectGlobalProperty } from '../../redux/propertySlice';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useNavigate } from 'react-router-dom';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import CreditScoreIcon from '@mui/icons-material/CreditScore';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { usePermissions } from '../../GlobalFunction/userPermissions';
import HomeIcon from '@mui/icons-material/Home';

const CreditNoteReg = () => {
    const theme = useTheme();
    const currentDate = dayjs();
    const [fromDate, setFromDate] = useState(dayjs().subtract(1, 'month'));
    const [toDate, setToDate] = useState(dayjs());
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [filteredRecords, setFilteredRecords] = useState([]);
    const [searchText, setSearchText] = useState('');
    const contentRef = useRef();
    const globalProperty = useSelector(selectGlobalProperty);
    const permissions = usePermissions("Credit Note Register");
    const { ViewRec = "0" } = permissions || {};

    const navigate = useNavigate();
    const handlePrint = useReactToPrint({
        contentRef,
        documentTitle: `Credit Note Register`,
        pageStyle: `
        @page { 
            size: A4 portrait; 
            margin: 1.2cm;
            @top-center { content: "Credit Note Register Report"; font-weight: bold; font-size: 24px; color: #635bff }
            @bottom-center { content: "Page " counter(page) " of " counter(pages); font-size: 10px; }
        }
        @media print {
            body { 
                font-family: Arial, sans-serif; 
                margin: 0; 
                -webkit-print-color-adjust: exact; 
                print-color-adjust: exact; 
            }
            .no-print { display: none !important; }
            .print-header { 
                text-align: center; 
                margin-bottom: 16px; 
                page-break-after: avoid;
            }
            .print-title { 
                font-size: 18px; 
                font-weight: bold; 
                margin: 4px 0; 
                color: #635bff;
            }
            .print-subtitle { 
                font-size: 12px; 
                color: #555; 
                margin: 3px 0;
            }
            table { 
                width: 100%; 
                border-collapse: collapse; 
                font-size: 11px; 
                page-break-inside: avoid;
            }
            th { 
                background: #635bff !important; 
                color: white !important; 
                padding: 3px 6px; 
                font-weight: bold;
                text-align: left;
            }
            td { 
                padding: 5px 8px; 
                border-bottom: 1px solid #eee;
            }
            tr:nth-child(even) td { 
                background-color: #f9f9f9; 
            }
            .total-row td { 
                background: #e8f5e9 !important; 
                font-weight: bold; 
                color: #1b5e20;
            }
            .net-amount { 
                font-weight: 700; 
                color: #e91e63; 
            }

             /* Show the total row only on the last page */
            @page :last {
                .total-row {
                    display: table-row !important;
                }
            }

            /* Hide total row from all other pages except the last page */
            @media print {
                .total-row {
                    display: none !important;
                }

                /* Show only on the last page */
                @page :last {
                    .total-row {
                        display: table-row !important;
                    }
                }
            }
        }
    `,
    });

    const fetchRecords = async () => {
        setLoading(true);
        try {
            const response = await axiosInstance.post('CnRpt/GetCnReport', {
                PropId: globalProperty,
                CnDt: fromDate.format('YYYY-MM-DD'),
                CnToDt: toDate.format('YYYY-MM-DD'),
                ResidentId: "",
                Flag: ''
            });
            if (response.data.Status === 0) {
                setRecords(response.data.Data || []);
                setFilteredRecords(response.data.Data || []);
            } else {
                toast.info(response.data.Message || 'No records found.');
            }
        } catch (error) {
            toast.error('Error fetching records from the server.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (globalProperty) {
            fetchRecords();
        }
    }, [globalProperty]);

    useEffect(() => {
        const searchWords = searchText.trim().toLowerCase().split(/\s+/);
        const filtered = records.filter((cnReg) => {
            const residentWords = cnReg.ResidentName?.toLowerCase().split(/\s+/) || [];
            const cnNum = cnReg.CnNo?.toLowerCase().split(/\s+/) || [];
            const propWords = cnReg.PropName?.toLowerCase().split(/\s+/) || [];
            const mobNo = cnReg.MobileNo?.toLowerCase().split(/\s+/) || [];
            const book = cnReg.BookNo?.toLowerCase().split(/\s+/) || [];
            const billDate = dayjs(cnReg.CnDt).format('DD-MM-YYYY').toLowerCase();
            const cnAmount = cnReg.CnAmt?.toString();
            const onAmount = cnReg.OnacAmt?.toString();
            const onAccBal = cnReg.OnacBal?.toString();

            return searchWords.every((word) =>
                residentWords.some((w) => w.includes(word)) ||
                cnNum.some((w) => w.includes(word)) ||
                propWords.some((w) => w.includes(word)) ||
                mobNo.some((w) => w.includes(word)) ||
                book.some((w) => w.includes(word)) ||
                billDate.includes(word) ||
                cnAmount.includes(word) ||
                onAmount.includes(word) ||
                onAccBal.includes(word)
            );
        });

        setFilteredRecords(filtered);
    }, [searchText, records]);

    const handleLoadRecords = () => {
        fetchRecords();
    };

    const total = filteredRecords.reduce((acc, r) => acc + Number(r.CnAmt || 0), 0);

    const handleExportToExcel = () => {
        if (filteredRecords.length === 0) return;

        const exportData = filteredRecords.map(cnReg => ({
            'Property': cnReg.PropName,
            'CN.No': cnReg.CnNo,
            'CNDate': dayjs(cnReg.CnDt).format('DD-MM-YYYY'),
            'CNAmt': cnReg.CnAmt,
            'AgstBill': cnReg.AgstBill,
            'OnacAmt': cnReg.OnacAmt,
            'OnacBal': cnReg.OnacBal,
            'Resident': cnReg.ResidentName,
            'Remark': cnReg.Remark,
            'ClgDate': dayjs(cnReg.ClgDt).format('DD-MM-YYYY'),
            'Amount': Number(cnReg.CnAmt),
        }));

        // Add total row
        exportData.push({
            'Property': '',
            'CN.No': 'TOTAL',
            'CNDate': '',
            'CNAmt': '',
            'AgstBill': '',
            'OnacAmt': '',
            'OnacBal': '',
            'Resident': `Records: ${filteredRecords.length}`,
            'Remark': '',
            'ClgDate': '',
            'Amount': total,
        });

        const ws = XLSX.utils.json_to_sheet(exportData, {
            header: ['Property', 'CN.No', 'CNDate', 'CNAmt', 'AgstBill', 'OnacAmt', 'OnacBal', 'Resident', 'Remark', 'ClgDate', 'Amount'],
        });

        const colWidths = [
            { wch: 20 }, // Property
            { wch: 12 }, // CNNo
            { wch: 12 }, // CNDate
            { wch: 12 }, // CNAmount
            { wch: 14 }, // AgstBill
            { wch: 12 }, // OnacAmt
            { wch: 12 }, // OnacBal
            { wch: 22 }, // Resident
            { wch: 12 }, // Remark
            { wch: 12 }, // ClgDate
            { wch: 12 }, // Amount
        ];
        ws['!cols'] = colWidths;

        const range = XLSX.utils.decode_range(ws['!ref'] || '');
        for (let R = 0; R <= range.e.r; ++R) {
            const netCell = ws[XLSX.utils.encode_cell({ c: 10, r: R })];
            if (netCell && typeof netCell.v === 'number') {
                netCell.z = '₹#,##0';
            }
        }

        // Bold total row (for last row)
        const totalRow = range.e.r;
        for (let C = 0; C <= 10; ++C) {
            const cell = ws[XLSX.utils.encode_cell({ c: C, r: totalRow })];
            if (cell) {
                cell.s = { font: { bold: true } };
            }
        }

        // Create a new workbook and append the sheet
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Credit Note Register');

        // Generate the file
        const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        const fileData = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const fileName = `CreditNote_${currentDate.format('YYYYMMDD')}.xlsx`;
        saveAs(fileData, fileName);
    };

    return (
        <Box sx={{
            width: '100%',
            height: '90vh',
            padding: '10px',
            backgroundColor: 'rgb(249, 249, 249)',
            position: 'relative'
        }}>
            <Box
                sx={{
                    backgroundColor: "#ffffff",
                    padding: { xs: "8px 16px", sm: "12px 16px" },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    boxShadow: "0 2px 10px rgba(0, 0, 0, 0.05)",
                    borderRadius: "12px",
                    margin: "10px auto",
                    maxWidth: "95%",
                    flexDirection: { xs: "column", sm: "column", md: "row" },
                    gap: 2,
                }}
            >
                <Breadcrumbs
                    aria-label="breadcrumb"
                    separator="›"
                    sx={{
                        fontSize: { xs: "14px", sm: "16px" },
                        display: "flex",
                        flexDirection: { xs: "column", sm: "column", md: "row" }
                    }}
                >
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500", display: "flex", alignItems: "center" }}
                        onClick={() => navigate('/dashboard')}
                    >
                        <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                        <Box component="span">Home</Box>
                    </Link>
                    <Link
                        underline="hover"
                        color="#635bff"
                        sx={{ cursor: "pointer", fontWeight: "500" }}
                        onClick={() => navigate('/reports/accountsFinance')}
                    >
                        Account & Finance
                    </Link>

                    <Typography color="text.primary" sx={{ fontWeight: "500" }}>
                        Credit Note Register
                    </Typography>
                </Breadcrumbs>

                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        spacing={1}
                        sx={{
                            flexWrap: "nowrap",
                            alignItems: "center",
                            justifyContent: { xs: "flex-start", md: "flex-end" },
                            width: "auto",
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={1}
                            sx={{
                                width: { xs: '100%', md: 'auto' },
                                justifyContent: { xs: 'space-between', md: 'flex-start' },
                                flexWrap: { xs: 'nowrap', md: 'nowrap' },
                            }}
                        >
                            <DatePicker
                                label="From Date"
                                value={fromDate}
                                onChange={(newValue) => setFromDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                maxDate={toDate}
                                format="DD/MM/YYYY"
                            />
                            <DatePicker
                                label="To Date"
                                value={toDate}
                                onChange={(newValue) => setToDate(newValue)}
                                slotProps={{
                                    textField: {
                                        size: 'small',
                                        sx: {
                                            flex: { xs: '0 1 48%', md: '0 1 160px' },
                                            bgcolor: 'white',
                                            borderRadius: 2,
                                            '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                            '& .MuiOutlinedInput-input': { padding: '4px 8px' },
                                        },
                                    },
                                }}
                                minDate={fromDate}
                                format="DD/MM/YYYY"
                            />
                        </Stack>
                        <Stack direction="row" spacing={1}
                            sx={{
                                mt: { xs: 1, md: 0 },
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                width: { xs: '100%', md: 'auto' },
                            }}
                        >
                            <Tooltip title='Retrieve data' arrow>
                                <span>
                                    <Button
                                        variant="contained"
                                        onClick={handleLoadRecords}
                                        disabled={!fromDate || !toDate}
                                        sx={{
                                            height: 30,
                                            bgcolor: '#635bff',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                bgcolor: '#635bff',
                                            },
                                        }}
                                    >
                                        Get Data
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Print bill records' arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<PrintIcon />}
                                        onClick={handlePrint}
                                        disabled={records.length === 0}
                                        sx={{
                                            height: 30,
                                            borderColor: 'secondary.main',
                                            color: 'secondary.main',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                borderColor: 'secondary.dark',
                                                boxShadow: theme.shadows[4],
                                                transform: 'translateY(-1px)',
                                                transition: 'all 0.2s ease-in-out',
                                            },
                                        }}
                                    >
                                        Print
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Export to excel' arrow>
                                <span>
                                    <Button
                                        variant="outlined"
                                        startIcon={<ImportExportIcon />}
                                        onClick={handleExportToExcel}
                                        disabled={records.length === 0}
                                        sx={{
                                            height: 30,
                                            borderColor: 'success.main',
                                            color: 'success.main',
                                            fontSize: '0.8rem',
                                            fontWeight: 'medium',
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            px: 2,
                                            '&:hover': {
                                                borderColor: 'success.dark',
                                                boxShadow: theme.shadows[4],
                                                transform: 'translateY(-1px)',
                                                transition: 'all 0.2s ease-in-out',
                                            },
                                        }}
                                    >
                                        Export
                                    </Button>
                                </span>
                            </Tooltip>
                            <Tooltip title='Previous Page' arrow>
                                <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#635bff', ":hover": { bgcolor: '#635bff' } }}
                                    onClick={() => navigate('/reports/accountsFinance')}
                                >
                                    <ArrowForwardIcon />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                    </Stack>
                </LocalizationProvider>
            </Box>

            <Container maxWidth={false}
                sx={{
                    width: '95%', mt: 2, p: 3, pb: 1, bgcolor: 'grey.50', borderRadius: 4,
                    boxShadow: theme.shadows[10],
                    mx: 'auto',
                }}
            >
                <ToastContainer />
                <Box sx={{ displayPrint: 'none' }}>
                    {/* <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', md: 'center' },
                            mb: 2,
                        }}
                    >
                        <Zoom in timeout={1000}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: { xs: 2, md: 0 },
                                    flexShrink: 0,
                                    minWidth: 0,
                                }}
                            >
                                <CreditScoreIcon sx={{ fontSize: 30, color: 'warning.main', mr: 1 }} />
                                <Typography variant="h5" noWrap
                                    sx={{
                                        fontWeight: 'bold',
                                        color: 'primary.dark',
                                        letterSpacing: 1,
                                        textTransform: 'none',
                                        background: `linear-gradient(90deg, ${theme.palette.info.main},${theme.palette.success.main}, ${theme.palette.error.main})`,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        userSelect: 'none'
                                    }}
                                >
                                    Credit Note Register
                                </Typography>
                            </Box>
                        </Zoom>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <Stack
                                direction={{ xs: 'column', md: 'row' }}
                                spacing={1}
                                sx={{
                                    justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                    alignItems: { xs: 'stretch', md: 'center' },
                                    width: '100%',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <Stack
                                    direction="row"
                                    spacing={1}
                                    sx={{
                                        width: { xs: '100%', md: 'auto' },
                                        justifyContent: { xs: 'space-between', md: 'flex-start' },
                                        flexWrap: { xs: 'nowrap', md: 'nowrap' },
                                    }}
                                >
                                    <DatePicker
                                        label="From Date"
                                        value={fromDate}
                                        onChange={(newValue) => setFromDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                sx: {
                                                    minWidth: { xs: '48%', md: 160 },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '4px 8px',
                                                    },
                                                },
                                            },
                                        }}
                                        maxDate={toDate}
                                        format="DD/MM/YYYY"
                                    />
                                    <DatePicker
                                        label="To Date"
                                        value={toDate}
                                        onChange={(newValue) => setToDate(newValue)}
                                        slotProps={{
                                            textField: {
                                                size: 'small',
                                                fullWidth: true,
                                                sx: {
                                                    minWidth: { xs: '48%', md: 160 },
                                                    bgcolor: 'white',
                                                    borderRadius: 2,
                                                    '& .MuiInputBase-root': { fontSize: '1rem', py: 0.2 },
                                                    '& .MuiOutlinedInput-input': {
                                                        padding: '4px 8px',
                                                    },
                                                },
                                            },
                                        }}
                                        minDate={fromDate}
                                        format="DD/MM/YYYY"
                                    />
                                </Stack>
                                <Stack direction="row" spacing={1}
                                    sx={{
                                        mt: { xs: 1, md: 0 },
                                        justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                        width: { xs: '100%', md: 'auto' },
                                    }}
                                >
                                    <Tooltip title='Retrieve data' arrow>
                                        <span>
                                            <Button
                                                variant="contained"
                                                onClick={handleLoadRecords}
                                                disabled={!fromDate || !toDate}
                                                sx={{
                                                    height: 30,
                                                    bgcolor: 'primary.main',
                                                    color: 'white',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        bgcolor: 'primary.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Get Data
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Print bill records' arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PrintIcon />}
                                                onClick={handlePrint}
                                                disabled={records.length === 0}
                                                sx={{
                                                    height: 30,
                                                    borderColor: 'secondary.main',
                                                    color: 'secondary.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        borderColor: 'secondary.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Print
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Export to excel' arrow>
                                        <span>
                                            <Button
                                                variant="outlined"
                                                startIcon={<ImportExportIcon />}
                                                onClick={handleExportToExcel}
                                                disabled={records.length === 0}
                                                sx={{
                                                    height: 30,
                                                    borderColor: 'success.main',
                                                    color: 'success.main',
                                                    fontSize: '0.8rem',
                                                    fontWeight: 'medium',
                                                    borderRadius: 2,
                                                    textTransform: 'none',
                                                    px: 2,
                                                    '&:hover': {
                                                        borderColor: 'success.dark',
                                                        boxShadow: theme.shadows[4],
                                                        transform: 'translateY(-1px)',
                                                        transition: 'all 0.2s ease-in-out',
                                                    },
                                                }}
                                            >
                                                Export
                                            </Button>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title='Previous Page' arrow>
                                        <IconButton sx={{ width: 32, height: 32, color: '#ffffff', bgcolor: '#635bff', ":hover": { bgcolor: '#635bff' } }}
                                            onClick={() => navigate('/reports/accountsFinance')}
                                        >
                                            <ArrowForwardIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Stack>
                        </LocalizationProvider>
                    </Box> */}

                    {/* Search Bar */}
                    <Box sx={{ mb: 1 }}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search by Resident, Mobile No, Booking No, Amount, etc."
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon sx={{ color: 'primary.main', fontSize: '1.2rem' }} />
                                    </InputAdornment>
                                ),
                                sx: {
                                    bgcolor: 'white',
                                    borderRadius: 2,
                                    '& .MuiInputBase-input': { fontSize: '0.85rem', py: 0.8 },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.main',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'primary.dark',
                                        boxShadow: theme.shadows[2],
                                    },
                                },
                            }}
                            sx={{ maxWidth: '100vw', mx: 'auto' }}
                        />
                    </Box>

                    <Divider sx={{ mb: 1, borderColor: 'grey.200', borderWidth: 1 }} />

                    {loading ? (
                        <Box sx={{ width: '100%', mb: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <CircularProgress size="3rem" sx={{ mb: 1 }} />
                            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                Loading data...
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{
                                maxHeight: '60vh',
                                overflowY: 'auto',
                                overflowX: 'auto',
                                position: 'relative',
                                '@media print': {
                                    maxHeight: 'none',
                                    overflow: 'visible',
                                },
                            }}
                            ref={contentRef}
                        >
                            {filteredRecords.length === 0 ? (
                                <Fade in timeout={700}>
                                    <Typography
                                        sx={{
                                            textAlign: 'center',
                                            color: 'text.secondary',
                                            py: 4,
                                            fontSize: '0.9rem',
                                            fontWeight: 'medium',
                                            fontStyle: 'italic',
                                        }}
                                    >
                                        No record found for the selected date range or search criteria. Please adjust and try again.
                                    </Typography>
                                </Fade>
                            ) : (
                                <Fade in timeout={700}>
                                    <Table sx={{ width: '100%' }}>
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    'Property', 'Resident', 'BookNo', 'CN.No', 'Mobile', 'CNDate', 'CNAmt', 'AgstBill', 'OnacAmt',
                                                    'OnacBal', 'Remark', 'ClearingDt', 'NetAmt'
                                                ].map((header, idx) => (
                                                    <TableCell
                                                        key={idx}
                                                        sx={{
                                                            background: '#635bff',
                                                            color: '#fff',
                                                            fontWeight: 700,
                                                            fontSize: '0.85rem',
                                                            position: 'sticky',
                                                            padding: '4px',
                                                            top: 0,
                                                            zIndex: 11,
                                                            boxShadow: '0 3px 6px rgba(0,0,0,0.15)',
                                                            textAlign: 'left',
                                                            textTransform: 'none',
                                                            letterSpacing: '0.5px',
                                                        }}
                                                    >
                                                        {header}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {filteredRecords.map((cnReg, idx) => (
                                                <TableRow
                                                    key={cnReg.ChkOutStlNo || idx}
                                                    sx={{
                                                        backgroundColor: idx % 2 === 0 ? '#f9f9f9ff' : '#fff',
                                                        transition: 'background 0.2s',
                                                        '&:hover': {
                                                            backgroundColor: '#e3f2fd',
                                                        },
                                                    }}
                                                >
                                                    <TableCell sx={cellStyle}>{cnReg.PropName}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.ResidentName}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.BookNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.CnNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.MobileNo}</TableCell>
                                                    <TableCell sx={cellStyle}>{dayjs(cnReg.CnDt).format('DD-MM-YYYY')}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.CnAmt}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.AgstBill}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.OnacAmt}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.OnacBal}</TableCell>
                                                    <TableCell sx={cellStyle}>{cnReg.Remark || "Not Found"}</TableCell>
                                                    <TableCell sx={cellStyle}>{dayjs(cnReg.ClgDt).format('DD-MM-YYYY')}</TableCell>
                                                    <TableCell sx={{ ...cellStyle, fontWeight: 700, color: theme.palette.secondary.main }}>
                                                        ₹{Number(cnReg.NetAmt).toLocaleString('en-IN')}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>

                                        <TableFooter>
                                            <TableRow className="total-row">
                                                <TotalCell colSpan={12}>
                                                    Total Records: {filteredRecords.length}
                                                </TotalCell>
                                                <TotalCell colSpan={12} sx={{ fontWeight: 800, textAlign: 'right', color: '#1b5e20' }}>
                                                    Total: ₹{total.toLocaleString('en-IN')}
                                                </TotalCell>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                </Fade>
                            )}
                        </Box>
                    )}
                </Box>
            </Container>
        </Box>
    );
};

// Compact cell style – reduced height
const cellStyle = {
    padding: '3px 6px',
    fontSize: '0.82rem',
    borderBottom: '1px solid #e0e0e0',
    whiteSpace: 'nowrap',
};

const TotalCell = styled(TableCell)(({ theme }) => ({
    ...cellStyle,
    fontWeight: 700,
    backgroundColor: '#e8f5e9',
    color: '#000',
    position: 'sticky',
    bottom: 0,
    zIndex: 10,
    boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
}));

export default CreditNoteReg;